import type { CSSProperties, ReactElement } from "react";
import type { OverlayId } from "@/lib/date-overlays";

const range = (count: number): number[] => Array.from({ length: count }, (_, index) => index);

const sequencedRandom = (seed: number): number => {
  const x = Math.sin(seed * 9999) * 10000;
  return x - Math.floor(x);
};

const PI_DIGITS =
  "3.14159265358979323846264338327950288419716939937510582097494459230781640628620899862803482534211706798214808651328230664709384460955058223172535940812848111745028410270193852110555964462294895493038196 ";

const upcomingGregorianYear = (): number => {
  const now = new Date();
  const month = now.getMonth();
  return now.getFullYear() + (month === 11 ? 1 : 0);
};

const upcomingHebrewYear = (): number => upcomingGregorianYear() + 3760;

interface FloatingParticleProps {
  count: number;
  baseClass: string;
  durationRange: [number, number];
  delayRange?: [number, number];
  sizeRange?: [number, number];
  renderItem: (index: number) => ReactElement;
  startFromBottom?: boolean;
  drift?: boolean;
}

const FloatingParticles = ({
  count,
  baseClass,
  durationRange,
  delayRange = [0, 8],
  sizeRange = [12, 20],
  renderItem,
  startFromBottom = false,
  drift = false,
}: FloatingParticleProps) => (
  <>
    {range(count).map((index) => {
      const left = sequencedRandom(index + 1) * 100;
      const duration =
        durationRange[0] + sequencedRandom(index + 17) * (durationRange[1] - durationRange[0]);
      const delay = delayRange[0] + sequencedRandom(index + 31) * (delayRange[1] - delayRange[0]);
      const size = sizeRange[0] + sequencedRandom(index + 47) * (sizeRange[1] - sizeRange[0]);
      const driftX = drift ? `${(sequencedRandom(index + 53) - 0.5) * 80}px` : "0px";
      const style: CSSProperties = {
        left: `${left}%`,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`,
        width: `${size}px`,
        height: `${size}px`,
        ["--holiday-drift" as string]: driftX,
      };
      if (startFromBottom) {
        style.bottom = "-40px";
        style.top = "auto";
      }
      return (
        <div key={index} className={baseClass} style={style}>
          {renderItem(index)}
        </div>
      );
    })}
  </>
);

interface HorizontalGliderProps {
  count: number;
  reverse?: boolean;
  durationRange: [number, number];
  delayRange?: [number, number];
  sizeRange?: [number, number];
  topRange?: [number, number];
  renderItem: (index: number) => ReactElement;
}

const HorizontalGliders = ({
  count,
  reverse = false,
  durationRange,
  delayRange = [0, 10],
  sizeRange = [24, 40],
  topRange = [5, 70],
  renderItem,
}: HorizontalGliderProps) => {
  const animationClass = reverse ? "holiday-glide-rtl" : "holiday-glide-ltr";
  return (
    <>
      {range(count).map((index) => {
        const top = topRange[0] + sequencedRandom(index + 71) * (topRange[1] - topRange[0]);
        const duration =
          durationRange[0] + sequencedRandom(index + 83) * (durationRange[1] - durationRange[0]);
        const delay = delayRange[0] + sequencedRandom(index + 97) * (delayRange[1] - delayRange[0]);
        const size = sizeRange[0] + sequencedRandom(index + 109) * (sizeRange[1] - sizeRange[0]);
        const style: CSSProperties = {
          top: `${top}%`,
          left: 0,
          animationDuration: `${duration}s`,
          animationDelay: `${delay}s`,
          width: `${size}px`,
          height: `${size}px`,
        };
        return (
          <div key={index} className={`absolute ${animationClass}`} style={style}>
            {renderItem(index)}
          </div>
        );
      })}
    </>
  );
};

interface BurstProps {
  count: number;
  durationRange: [number, number];
  sizeRange?: [number, number];
  className?: string;
  renderItem: (index: number) => ReactElement;
}

const Bursts = ({
  count,
  durationRange,
  sizeRange = [60, 120],
  className = "holiday-burst",
  renderItem,
}: BurstProps) => (
  <>
    {range(count).map((index) => {
      const top = 5 + sequencedRandom(index + 131) * 75;
      const left = 5 + sequencedRandom(index + 137) * 85;
      const duration =
        durationRange[0] + sequencedRandom(index + 149) * (durationRange[1] - durationRange[0]);
      const delay = sequencedRandom(index + 157) * duration;
      const size = sizeRange[0] + sequencedRandom(index + 163) * (sizeRange[1] - sizeRange[0]);
      return (
        <div
          key={index}
          className={`absolute ${className}`}
          style={{
            top: `${top}%`,
            left: `${left}%`,
            width: `${size}px`,
            height: `${size}px`,
            animationDuration: `${duration}s`,
            animationDelay: `${delay}s`,
          }}
        >
          {renderItem(index)}
        </div>
      );
    })}
  </>
);

const ChristmasTree = () => (
  <svg viewBox="0 0 200 240" className="h-full w-full">
    <title>Christmas tree</title>
    <polygon
      points="100,20 60,100 85,100 50,170 90,170 40,220 160,220 110,170 150,170 115,100 140,100"
      fill="#0f7a3e"
      stroke="#0a5430"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <rect x="85" y="220" width="30" height="18" fill="#7a4a2a" stroke="#5a3520" strokeWidth="2" />
    <polygon
      points="100,5 108,22 126,22 112,32 117,48 100,38 83,48 88,32 74,22 92,22"
      fill="#facc15"
      stroke="#a16207"
      strokeWidth="1.8"
    />
    <path
      d="M 70 110 Q 100 130 130 110 Q 150 130 100 145 Q 50 130 70 110 Z"
      stroke="#facc15"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M 60 150 Q 100 170 140 150 Q 160 170 100 185 Q 40 170 60 150 Z"
      stroke="#facc15"
      strokeWidth="2"
      fill="none"
    />
    {[
      { x: 80, y: 115, c: "#dc2626" },
      { x: 95, y: 125, c: "#facc15" },
      { x: 115, y: 120, c: "#2563eb" },
      { x: 75, y: 155, c: "#facc15" },
      { x: 100, y: 165, c: "#dc2626" },
      { x: 130, y: 158, c: "#2563eb" },
      { x: 60, y: 195, c: "#facc15" },
      { x: 100, y: 205, c: "#dc2626" },
      { x: 140, y: 197, c: "#2563eb" },
    ].map((light) => (
      <circle
        key={`${light.x}-${light.y}`}
        cx={light.x}
        cy={light.y}
        r="4"
        fill={light.c}
        className="holiday-twinkle"
        style={{
          animationDelay: `${(light.x + light.y) * 0.01}s`,
          filter: `drop-shadow(0 0 4px ${light.c})`,
        }}
      />
    ))}
  </svg>
);

const Snowflake = () => (
  <svg viewBox="0 0 24 24" className="h-full w-full opacity-90">
    <title>Snow</title>
    <circle cx="12" cy="12" r="6" fill="white" />
  </svg>
);

const Sleigh = () => (
  <svg viewBox="0 0 220 100" className="h-full w-full">
    <title>Sleigh</title>
    <path d="M 10 70 Q 30 50 80 50 L 140 50 Q 160 50 170 70 L 30 70 Z" fill="#b91c1c" />
    <path d="M 5 75 Q 30 80 100 80 L 175 80 Q 180 75 175 72 L 10 72 Z" fill="#7c1d1d" />
    <circle cx="60" cy="45" r="10" fill="#fda4af" />
    <circle cx="55" cy="40" r="3" fill="#1a1a1a" />
    <path d="M 50 50 Q 55 54 60 50" stroke="#1a1a1a" strokeWidth="1.5" fill="none" />
    <path d="M 45 35 L 65 30 L 60 45 Z" fill="white" />
    <path d="M 170 70 Q 210 60 215 55" stroke="#7c1d1d" strokeWidth="2" fill="none" />
    <ellipse cx="208" cy="55" rx="8" ry="6" fill="#7c4a2a" />
    <line x1="200" y1="48" x2="195" y2="35" stroke="#1a1a1a" strokeWidth="1.5" />
    <line x1="208" y1="48" x2="208" y2="35" stroke="#1a1a1a" strokeWidth="1.5" />
  </svg>
);

const GiftBox = ({ index }: { index: number }) => {
  const palette = ["#dc2626", "#16a34a", "#2563eb", "#7c3aed", "#f59e0b"];
  const ribbonPalette = ["#facc15", "#dc2626", "#facc15", "#fde047", "#dc2626"];
  const color = palette[index % palette.length];
  const ribbon = ribbonPalette[index % ribbonPalette.length];
  return (
    <svg viewBox="0 0 60 60" className="h-full w-full">
      <title>Gift</title>
      <rect x="6" y="20" width="48" height="36" fill={color} stroke="#0a0a0a" strokeWidth="1" />
      <rect x="6" y="14" width="48" height="10" fill={color} stroke="#0a0a0a" strokeWidth="1" />
      <rect x="26" y="14" width="8" height="42" fill={ribbon} />
      <rect x="6" y="22" width="48" height="4" fill={ribbon} />
      <path d="M 30 14 Q 18 4 14 14 Z" fill={ribbon} />
      <path d="M 30 14 Q 42 4 46 14 Z" fill={ribbon} />
    </svg>
  );
};

const ChainedLights = () => (
  <div className="absolute left-0 right-0 top-2 h-8">
    <svg viewBox="0 0 1200 40" className="h-full w-full" preserveAspectRatio="none" aria-hidden>
      <title>Chained lights</title>
      <path
        d="M 0 8 Q 100 28 200 8 Q 300 28 400 8 Q 500 28 600 8 Q 700 28 800 8 Q 900 28 1000 8 Q 1100 28 1200 8"
        stroke="#16a34a"
        strokeWidth="2"
        fill="none"
      />
    </svg>
    <div className="absolute inset-x-0 top-1 flex justify-around">
      {range(28).map((index) => {
        const colors = ["#ef4444", "#facc15", "#22c55e", "#2563eb", "#a855f7"];
        const color = colors[index % colors.length];
        return (
          <div
            key={index}
            className="holiday-twinkle h-3 w-3 rounded-full"
            style={{
              backgroundColor: color,
              animationDelay: `${index * 0.12}s`,
              marginTop: index % 2 === 0 ? "0" : "10px",
              boxShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
            }}
          />
        );
      })}
    </div>
  </div>
);

const Pumpkin = () => (
  <svg viewBox="0 0 200 180" className="h-full w-full">
    <title>Pumpkin</title>
    <ellipse cx="60" cy="100" rx="30" ry="60" fill="#ea7317" />
    <ellipse cx="100" cy="100" rx="45" ry="68" fill="#f97316" />
    <ellipse cx="140" cy="100" rx="30" ry="60" fill="#ea7317" />
    <rect x="95" y="28" width="10" height="20" fill="#3f6212" />
    <path d="M 105 32 Q 130 20 135 40" stroke="#3f6212" strokeWidth="4" fill="none" />
    <polygon points="75,90 90,115 60,115" fill="#1a1a1a" />
    <polygon points="125,90 140,115 110,115" fill="#1a1a1a" />
    <path
      d="M 70 135 L 80 130 L 85 138 L 95 130 L 100 138 L 110 130 L 115 138 L 125 130 L 135 135 L 130 145 L 70 145 Z"
      fill="#1a1a1a"
    />
  </svg>
);

const Bat = () => (
  <svg viewBox="0 0 64 40" className="h-full w-full">
    <title>Bat</title>
    <path
      d="M 32 22 L 24 10 L 16 18 L 4 8 L 8 26 L 20 30 L 32 26 L 44 30 L 56 26 L 60 8 L 48 18 L 40 10 Z"
      fill="#1a1a1a"
    />
    <circle cx="28" cy="22" r="1.5" fill="#ef4444" />
    <circle cx="36" cy="22" r="1.5" fill="#ef4444" />
  </svg>
);

const Spider = () => (
  <svg viewBox="0 0 60 100" className="h-full w-full">
    <title>Spider</title>
    <line x1="30" y1="0" x2="30" y2="55" stroke="#9ca3af" strokeWidth="0.5" />
    <ellipse cx="30" cy="70" rx="14" ry="16" fill="#1a1a1a" />
    <circle cx="30" cy="58" r="7" fill="#1a1a1a" />
    <circle cx="27" cy="56" r="1.5" fill="#ef4444" />
    <circle cx="33" cy="56" r="1.5" fill="#ef4444" />
    <path d="M 16 65 Q 5 60 2 70" stroke="#1a1a1a" strokeWidth="2" fill="none" />
    <path d="M 16 70 Q 3 72 0 82" stroke="#1a1a1a" strokeWidth="2" fill="none" />
    <path d="M 16 75 Q 5 82 4 92" stroke="#1a1a1a" strokeWidth="2" fill="none" />
    <path d="M 16 80 Q 8 90 12 100" stroke="#1a1a1a" strokeWidth="2" fill="none" />
    <path d="M 44 65 Q 55 60 58 70" stroke="#1a1a1a" strokeWidth="2" fill="none" />
    <path d="M 44 70 Q 57 72 60 82" stroke="#1a1a1a" strokeWidth="2" fill="none" />
    <path d="M 44 75 Q 55 82 56 92" stroke="#1a1a1a" strokeWidth="2" fill="none" />
    <path d="M 44 80 Q 52 90 48 100" stroke="#1a1a1a" strokeWidth="2" fill="none" />
  </svg>
);

const Ghost = () => (
  <svg viewBox="0 0 40 50" className="h-full w-full">
    <title>Ghost</title>
    <path
      d="M 5 25 Q 5 5 20 5 Q 35 5 35 25 L 35 44 Q 33 50 30 44 Q 27 50 24 44 Q 21 50 18 44 Q 15 50 12 44 Q 9 50 7 44 Q 5 50 5 44 Z"
      fill="white"
      opacity="0.85"
    />
    <circle cx="15" cy="22" r="3" fill="#1a1a1a" />
    <circle cx="25" cy="22" r="3" fill="#1a1a1a" />
    <ellipse cx="20" cy="32" rx="3" ry="4" fill="#1a1a1a" />
  </svg>
);

const SpookyHouse = () => (
  <svg viewBox="0 0 220 180" className="h-full w-full">
    <title>Spooky house</title>
    <rect x="40" y="80" width="140" height="100" fill="#1f1f1f" />
    <polygon points="30,82 110,20 190,82" fill="#0f0f0f" />
    <polygon points="135,20 135,5 165,5 165,40" fill="#1f1f1f" />
    <rect x="100" y="120" width="22" height="50" fill="#facc15" opacity="0.85" />
    <line x1="111" y1="120" x2="111" y2="170" stroke="#0a0a0a" strokeWidth="1" />
    <line x1="100" y1="145" x2="122" y2="145" stroke="#0a0a0a" strokeWidth="1" />
    <polygon points="55,100 75,100 65,120" fill="#facc15" opacity="0.75" />
    <polygon points="145,100 165,100 155,120" fill="#facc15" opacity="0.75" />
    <line x1="110" y1="20" x2="110" y2="8" stroke="#1f1f1f" strokeWidth="2" />
    <polygon points="110,8 122,4 122,12" fill="#1f1f1f" />
    <rect x="55" y="155" width="14" height="25" fill="#0a0a0a" />
    <rect x="151" y="155" width="14" height="25" fill="#0a0a0a" />
  </svg>
);

const Heart = () => (
  <svg viewBox="0 0 24 24" className="h-full w-full">
    <title>Heart</title>
    <path
      d="M12 21s-7-4.5-9.5-9C0 8 2.5 3.5 7 4c2.5.3 4 2 5 3.5C13 6 14.5 4.3 17 4c4.5-.5 7 4 4.5 8-2.5 4.5-9.5 9-9.5 9z"
      fill="#ef4444"
    />
  </svg>
);

const PaperPlane = () => (
  <svg viewBox="0 0 90 50" className="h-full w-full">
    <title>Plane</title>
    <path d="M 70 25 L 18 8 L 28 25 L 18 42 Z" fill="#ef4444" stroke="#7c1d1d" strokeWidth="1.5" />
    <path d="M 28 25 L 60 22 L 70 25 L 60 28 Z" fill="#dc2626" />
    <circle cx="60" cy="25" r="3" fill="white" />
  </svg>
);

const ProposalBanner = ({ text }: { text: string }) => (
  <div className="absolute left-[-380px] top-1/2 flex h-16 w-[820px] -translate-y-1/2 items-center holiday-plane-fly">
    <div className="holiday-banner-wave flex h-12 w-[700px] items-center justify-center rounded-md border-4 border-pink-400 bg-pink-50 px-4 text-xl font-extrabold tracking-wide text-pink-700 shadow-lg">
      {text}
    </div>
    <div className="h-12 w-20">
      <PaperPlane />
    </div>
  </div>
);

const Shamrock = () => (
  <svg viewBox="0 0 24 24" className="h-full w-full">
    <title>Shamrock</title>
    <g fill="#16a34a">
      <ellipse cx="12" cy="7" rx="4" ry="5" />
      <ellipse cx="7" cy="12" rx="5" ry="4" />
      <ellipse cx="17" cy="12" rx="5" ry="4" />
      <ellipse cx="12" cy="17" rx="4" ry="5" />
    </g>
    <rect x="11" y="14" width="2" height="8" fill="#16a34a" />
  </svg>
);

const Rainbow = () => (
  <svg viewBox="0 0 600 200" className="h-full w-full" preserveAspectRatio="none">
    <title>Rainbow</title>
    {[
      { color: "#ef4444", r: 180 },
      { color: "#f97316", r: 165 },
      { color: "#facc15", r: 150 },
      { color: "#22c55e", r: 135 },
      { color: "#2563eb", r: 120 },
      { color: "#7c3aed", r: 105 },
    ].map((band) => (
      <path
        key={band.color}
        d={`M ${300 - band.r} 200 A ${band.r} ${band.r} 0 0 1 ${300 + band.r} 200`}
        stroke={band.color}
        strokeWidth="14"
        fill="none"
      />
    ))}
  </svg>
);

const PotOfGold = () => (
  <svg viewBox="0 0 160 140" className="h-full w-full">
    <title>Pot of gold</title>
    <path d="M 20 60 L 140 60 L 130 130 L 30 130 Z" fill="#1f1f1f" />
    <rect x="14" y="50" width="132" height="14" rx="3" fill="#0a0a0a" />
    <ellipse cx="80" cy="52" rx="60" ry="10" fill="#facc15" />
    <circle cx="60" cy="48" r="8" fill="#fde047" />
    <circle cx="80" cy="46" r="8" fill="#facc15" />
    <circle cx="100" cy="49" r="7" fill="#fde047" />
    <circle cx="72" cy="42" r="6" fill="#fde047" />
    <circle cx="92" cy="42" r="5" fill="#facc15" />
  </svg>
);

const RunningPi = () => (
  <svg viewBox="0 0 60 70" className="h-full w-full">
    <title>Running pi</title>
    <text
      x="30"
      y="42"
      textAnchor="middle"
      fill="#f59e0b"
      fontSize="44"
      fontFamily="serif"
      fontWeight="bold"
    >
      π
    </text>
    <circle cx="22" cy="20" r="2.5" fill="#1a1a1a" />
    <circle cx="38" cy="20" r="2.5" fill="#1a1a1a" />
    <line x1="20" y1="52" x2="14" y2="65" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
    <line x1="40" y1="52" x2="46" y2="65" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const MatrixColumn = ({ index }: { index: number }) => {
  const digits = range(28).map((row) => Math.floor(sequencedRandom(index * 100 + row) * 10));
  return (
    <div className="flex flex-col font-mono text-xs leading-tight text-amber-500/40">
      {digits.map((digit, row) => (
        <span key={row} style={{ opacity: 1 - row / 32 }}>
          {digit}
        </span>
      ))}
    </div>
  );
};

const PiDigitMarquee = () => (
  <div className="absolute left-0 right-0 top-[40%] overflow-hidden">
    <div className="holiday-marquee whitespace-nowrap font-mono text-3xl font-bold tracking-wider text-amber-500/70">
      {PI_DIGITS}
      {PI_DIGITS}
    </div>
  </div>
);

const Menorah = () => (
  <svg viewBox="0 0 260 220" className="h-full w-full">
    <title>Menorah</title>
    {range(9).map((index) => {
      const x = 30 + index * 25;
      const baseY = 180;
      const top = index === 4 ? 60 : 90;
      return (
        <line key={index} x1={x} y1={baseY} x2={x} y2={top} stroke="#d4a64a" strokeWidth="4" />
      );
    })}
    <path
      d="M 30 180 Q 30 120 130 120 Q 230 120 230 180"
      stroke="#d4a64a"
      strokeWidth="5"
      fill="none"
    />
    <rect x="100" y="180" width="60" height="20" fill="#d4a64a" />
    <rect x="80" y="200" width="100" height="12" fill="#a16207" />
    {range(9).map((index) => {
      const x = 30 + index * 25;
      const top = index === 4 ? 60 : 90;
      return (
        <g key={index}>
          <rect x={x - 3} y={top - 8} width="6" height="8" fill="#facc15" />
          <ellipse
            cx={x}
            cy={top - 16}
            rx="5"
            ry="9"
            fill="#fef08a"
            className="holiday-flame"
            style={{ animationDelay: `${index * 0.2}s`, transformOrigin: `${x}px ${top - 8}px` }}
          />
          <ellipse cx={x} cy={top - 18} rx="2.5" ry="5" fill="#f97316" />
        </g>
      );
    })}
  </svg>
);

const Sevivon = () => (
  <svg viewBox="0 0 40 50" className="h-full w-full">
    <title>Sevivon</title>
    <rect x="10" y="2" width="20" height="6" fill="#0ea5e9" />
    <rect x="6" y="8" width="28" height="22" fill="#2563eb" />
    <polygon points="6,30 34,30 20,46" fill="#1e40af" />
    <text x="20" y="24" textAnchor="middle" fill="white" fontSize="12" fontFamily="serif">
      נ
    </text>
  </svg>
);

const Sufganiya = () => (
  <svg viewBox="0 0 40 40" className="h-full w-full">
    <title>Sufganiya</title>
    <circle cx="20" cy="20" r="16" fill="#d97706" />
    <path d="M 4 20 Q 20 28 36 20" stroke="#b45309" strokeWidth="0.6" fill="none" />
    <ellipse cx="20" cy="13" rx="9" ry="4" fill="#dc2626" />
    <circle cx="14" cy="11" r="1" fill="white" opacity="0.8" />
    <circle cx="22" cy="12" r="1" fill="white" opacity="0.8" />
    <circle cx="18" cy="14" r="0.8" fill="white" opacity="0.7" />
  </svg>
);

const Gelt = () => (
  <svg viewBox="0 0 30 30" className="h-full w-full">
    <title>Gelt coin</title>
    <circle cx="15" cy="15" r="13" fill="#fde047" stroke="#a16207" strokeWidth="1.5" />
    <text x="15" y="20" textAnchor="middle" fill="#a16207" fontSize="14" fontFamily="serif">
      ח
    </text>
  </svg>
);

const StarOfDavid = () => (
  <svg viewBox="0 0 24 24" className="h-full w-full">
    <title>Star of David</title>
    <polygon
      points="12,2 20.66,17 3.34,17"
      fill="none"
      stroke="#2563eb"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <polygon
      points="12,22 3.34,7 20.66,7"
      fill="none"
      stroke="#2563eb"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const Matzah = () => (
  <svg viewBox="0 0 32 32" className="h-full w-full">
    <title>Matzah</title>
    <rect
      x="2"
      y="2"
      width="28"
      height="28"
      rx="2"
      fill="#d4a64a"
      stroke="#92670a"
      strokeWidth="1.5"
    />
    {range(6).map((index) => (
      <circle
        key={index}
        cx={6 + (index % 3) * 10}
        cy={8 + Math.floor(index / 3) * 10}
        r="1.2"
        fill="#7c4a03"
      />
    ))}
  </svg>
);

const WineCup = () => (
  <svg viewBox="0 0 40 60" className="h-full w-full">
    <title>Wine cup</title>
    <path
      d="M 8 10 L 32 10 Q 32 28 24 32 L 24 48 L 30 50 L 30 54 L 10 54 L 10 50 L 16 48 L 16 32 Q 8 28 8 10 Z"
      fill="#fef3c7"
      stroke="#92670a"
      strokeWidth="1.5"
    />
    <path d="M 10 12 L 30 12 Q 30 25 24 28 L 16 28 Q 10 25 10 12 Z" fill="#b91c1c" />
  </svg>
);

const Frog = () => (
  <svg viewBox="0 0 60 40" className="h-full w-full">
    <title>Frog</title>
    <ellipse cx="30" cy="28" rx="22" ry="12" fill="#16a34a" />
    <circle cx="20" cy="14" r="8" fill="#16a34a" />
    <circle cx="40" cy="14" r="8" fill="#16a34a" />
    <circle cx="20" cy="13" r="3.5" fill="white" />
    <circle cx="40" cy="13" r="3.5" fill="white" />
    <circle cx="20" cy="13" r="2" fill="#1a1a1a" />
    <circle cx="40" cy="13" r="2" fill="#1a1a1a" />
    <path d="M 22 30 Q 30 36 38 30" stroke="#0a4a1a" strokeWidth="1.5" fill="none" />
  </svg>
);

const Pyramid = () => (
  <svg viewBox="0 0 240 140" className="h-full w-full">
    <title>Pyramid</title>
    <polygon points="120,10 30,135 210,135" fill="#d4a64a" />
    <polygon points="120,10 120,135 210,135" fill="#a16207" opacity="0.55" />
    <line x1="120" y1="10" x2="120" y2="135" stroke="#7c4a03" strokeWidth="1" />
  </svg>
);

const HaggadahScroll = () => (
  <svg viewBox="0 0 80 100" className="h-full w-full">
    <title>Haggadah scroll</title>
    <rect
      x="10"
      y="10"
      width="60"
      height="80"
      rx="3"
      fill="#fef3c7"
      stroke="#92670a"
      strokeWidth="1.5"
    />
    <line x1="20" y1="22" x2="60" y2="22" stroke="#92670a" strokeWidth="0.8" />
    <line x1="20" y1="30" x2="60" y2="30" stroke="#92670a" strokeWidth="0.8" />
    <line x1="20" y1="38" x2="60" y2="38" stroke="#92670a" strokeWidth="0.8" />
    <text x="40" y="62" textAnchor="middle" fill="#92670a" fontSize="14" fontFamily="serif">
      הגדה
    </text>
    <line x1="20" y1="74" x2="60" y2="74" stroke="#92670a" strokeWidth="0.8" />
    <line x1="20" y1="82" x2="60" y2="82" stroke="#92670a" strokeWidth="0.8" />
    <rect x="6" y="6" width="68" height="6" rx="3" fill="#a16207" />
    <rect x="6" y="88" width="68" height="6" rx="3" fill="#a16207" />
  </svg>
);

const PomegranateSeed = () => (
  <svg viewBox="0 0 12 16" className="h-full w-full">
    <title>Pomegranate seed</title>
    <ellipse cx="6" cy="8" rx="4" ry="6" fill="#b91c1c" />
    <ellipse cx="5" cy="6" rx="1" ry="2" fill="#fca5a5" opacity="0.6" />
  </svg>
);

const HoneyDrop = () => (
  <svg viewBox="0 0 16 24" className="h-full w-full">
    <title>Honey drop</title>
    <path d="M 8 2 Q 14 14 8 22 Q 2 14 8 2 Z" fill="#facc15" stroke="#a16207" strokeWidth="0.8" />
    <ellipse cx="6" cy="10" rx="1.5" ry="3" fill="#fef9c3" opacity="0.7" />
  </svg>
);

const AppleHoney = () => (
  <svg viewBox="0 0 220 220" className="h-full w-full">
    <title>Apple and honey</title>
    <path
      d="M 60 100 Q 30 110 40 160 Q 50 200 90 200 Q 110 195 110 180 Q 110 195 130 200 Q 170 200 180 160 Q 190 110 160 100 Q 130 95 110 110 Q 90 95 60 100 Z"
      fill="#dc2626"
    />
    <path d="M 105 95 Q 105 80 110 70" stroke="#5a3520" strokeWidth="4" fill="none" />
    <path d="M 110 80 Q 130 70 140 80 Q 125 85 115 90" fill="#16a34a" />
    <ellipse cx="110" cy="155" rx="40" ry="12" fill="#facc15" opacity="0.85" />
    <path
      d="M 110 155 Q 115 175 110 195 Q 105 200 110 205"
      stroke="#facc15"
      strokeWidth="5"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const Shofar = () => (
  <svg viewBox="0 0 200 100" className="h-full w-full">
    <title>Shofar</title>
    <path
      d="M 10 60 Q 60 50 110 55 Q 160 60 190 30 Q 195 40 185 55 Q 150 80 100 75 Q 50 70 10 75 Z"
      fill="#fef3c7"
      stroke="#92670a"
      strokeWidth="2"
    />
    <path d="M 30 65 Q 80 62 130 64" stroke="#a16207" strokeWidth="0.6" fill="none" />
    <path d="M 30 70 Q 80 67 130 69" stroke="#a16207" strokeWidth="0.6" fill="none" />
  </svg>
);

const VenetianMask = () => (
  <svg viewBox="0 0 60 36" className="h-full w-full">
    <title>Venetian mask</title>
    <path
      d="M 4 18 Q 4 4 30 4 Q 56 4 56 18 Q 56 32 42 32 Q 30 32 30 24 Q 30 32 18 32 Q 4 32 4 18 Z"
      fill="#7c3aed"
    />
    <ellipse cx="18" cy="16" rx="5" ry="4" fill="#1a1a1a" />
    <ellipse cx="42" cy="16" rx="5" ry="4" fill="#1a1a1a" />
    <circle cx="30" cy="4" r="3" fill="#fbbf24" />
  </svg>
);

const HarlequinMask = () => (
  <svg viewBox="0 0 60 36" className="h-full w-full">
    <title>Harlequin mask</title>
    <path d="M 4 14 L 30 4 L 56 14 L 50 30 Q 30 36 10 30 Z" fill="#dc2626" />
    <polygon points="4,14 30,4 30,18 16,22" fill="#facc15" />
    <polygon points="56,14 30,4 30,18 44,22" fill="#2563eb" />
    <ellipse cx="20" cy="16" rx="4" ry="3" fill="#1a1a1a" />
    <ellipse cx="40" cy="16" rx="4" ry="3" fill="#1a1a1a" />
  </svg>
);

const ComedyMask = () => (
  <svg viewBox="0 0 60 36" className="h-full w-full">
    <title>Comedy mask</title>
    <ellipse cx="30" cy="18" rx="26" ry="14" fill="#fde047" stroke="#92670a" strokeWidth="2" />
    <circle cx="20" cy="16" r="3.5" fill="#1a1a1a" />
    <circle cx="40" cy="16" r="3.5" fill="#1a1a1a" />
    <path d="M 18 24 Q 30 32 42 24" stroke="#1a1a1a" strokeWidth="2" fill="none" />
  </svg>
);

const Hamantasch = () => (
  <svg viewBox="0 0 60 60" className="h-full w-full">
    <title>Hamantasch</title>
    <polygon points="30,8 54,52 6,52" fill="#d4a64a" stroke="#92670a" strokeWidth="2" />
    <ellipse cx="30" cy="36" rx="9" ry="6" fill="#5a2810" />
  </svg>
);

const Gragger = () => (
  <svg viewBox="0 0 60 60" className="h-full w-full">
    <title>Gragger</title>
    <rect x="24" y="38" width="12" height="20" fill="#7c4a2a" />
    <rect x="14" y="20" width="32" height="22" fill="#dc2626" />
    <path d="M 14 20 L 14 42 L 4 32 Z" fill="#b91c1c" />
    <path d="M 46 20 L 46 42 L 56 32 Z" fill="#b91c1c" />
    <circle cx="30" cy="31" r="3" fill="#facc15" />
  </svg>
);

const MishloachManot = () => (
  <svg viewBox="0 0 70 70" className="h-full w-full">
    <title>Mishloach Manot</title>
    <path d="M 6 28 Q 6 22 12 22 L 58 22 Q 64 22 64 28 L 56 64 L 14 64 Z" fill="#7c4a2a" />
    <ellipse cx="35" cy="24" rx="29" ry="4" fill="#5a3520" />
    <path
      d="M 6 28 Q 35 12 64 28"
      stroke="#facc15"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <circle cx="22" cy="18" r="6" fill="#ec4899" />
    <circle cx="35" cy="14" r="7" fill="#facc15" />
    <circle cx="48" cy="18" r="6" fill="#2563eb" />
    <polygon points="35,8 38,2 32,2" fill="#dc2626" />
  </svg>
);

const IsraelFlagBig = () => (
  <svg viewBox="0 0 240 160" className="h-full w-full">
    <title>Israel flag</title>
    <rect x="0" y="0" width="240" height="160" fill="white" />
    <rect x="0" y="16" width="240" height="22" fill="#2563eb" />
    <rect x="0" y="122" width="240" height="22" fill="#2563eb" />
    <polygon
      points="120,56 130,80 156,80 134,96 142,122 120,108 98,122 106,96 84,80 110,80"
      fill="none"
      stroke="#2563eb"
      strokeWidth="6"
    />
  </svg>
);

const FlagPole = () => (
  <svg viewBox="0 0 12 280" className="h-full w-full">
    <title>Flag pole</title>
    <rect x="5" y="0" width="2" height="280" fill="#9ca3af" />
    <circle cx="6" cy="4" r="4" fill="#facc15" />
  </svg>
);

const Dove = () => (
  <svg viewBox="0 0 60 40" className="h-full w-full">
    <title>Dove with olive branch</title>
    <ellipse cx="30" cy="22" rx="14" ry="9" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
    <circle cx="44" cy="16" r="6" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
    <path d="M 48 14 L 56 12 L 50 17 Z" fill="#fbbf24" />
    <circle cx="46" cy="15" r="0.8" fill="#1a1a1a" />
    <path d="M 16 18 Q 8 10 4 14 Q 12 16 18 22 Z" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
    <path d="M 38 28 Q 44 32 50 30" stroke="#16a34a" strokeWidth="1.5" fill="none" />
    <ellipse cx="42" cy="29" rx="2" ry="1" fill="#16a34a" />
    <ellipse cx="46" cy="30" rx="2" ry="1" fill="#16a34a" />
    <ellipse cx="50" cy="30" rx="2" ry="1" fill="#16a34a" />
  </svg>
);

const EasterEgg = ({ index }: { index: number }) => {
  const colors = ["#fbbf24", "#ec4899", "#60a5fa", "#a78bfa", "#34d399"];
  const fill = colors[index % colors.length];
  return (
    <svg viewBox="0 0 30 40" className="h-full w-full">
      <title>Easter egg</title>
      <ellipse cx="15" cy="22" rx="12" ry="17" fill={fill} />
      <path d="M 3 22 Q 9 18 15 22 Q 21 26 27 22" stroke="white" strokeWidth="2" fill="none" />
      <path d="M 3 28 Q 9 24 15 28 Q 21 32 27 28" stroke="white" strokeWidth="2" fill="none" />
      <circle cx="9" cy="14" r="1.5" fill="white" />
      <circle cx="21" cy="14" r="1.5" fill="white" />
    </svg>
  );
};

const Bunny = () => (
  <svg viewBox="0 0 60 60" className="h-full w-full">
    <title>Bunny</title>
    <ellipse cx="30" cy="42" rx="18" ry="14" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
    <circle cx="30" cy="28" r="11" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
    <ellipse cx="22" cy="12" rx="3" ry="10" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
    <ellipse cx="38" cy="12" rx="3" ry="10" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
    <ellipse cx="22" cy="12" rx="1.5" ry="6" fill="#fda4af" />
    <ellipse cx="38" cy="12" rx="1.5" ry="6" fill="#fda4af" />
    <circle cx="26" cy="26" r="1.5" fill="#1a1a1a" />
    <circle cx="34" cy="26" r="1.5" fill="#1a1a1a" />
    <circle cx="30" cy="31" r="1.5" fill="#fda4af" />
    <circle cx="48" cy="44" r="4" fill="white" stroke="#cbd5e1" strokeWidth="0.8" />
  </svg>
);

const Flower = ({ index }: { index: number }) => {
  const palette = ["#ec4899", "#facc15", "#60a5fa", "#a78bfa", "#f97316"];
  const fill = palette[index % palette.length];
  return (
    <svg viewBox="0 0 40 50" className="h-full w-full">
      <title>Flower</title>
      <rect x="19" y="20" width="2" height="30" fill="#16a34a" />
      <path d="M 20 30 Q 12 28 10 22" stroke="#16a34a" strokeWidth="2" fill="none" />
      <circle cx="20" cy="14" r="5" fill={fill} />
      <circle cx="12" cy="20" r="5" fill={fill} />
      <circle cx="28" cy="20" r="5" fill={fill} />
      <circle cx="14" cy="10" r="5" fill={fill} />
      <circle cx="26" cy="10" r="5" fill={fill} />
      <circle cx="20" cy="16" r="3" fill="#facc15" />
    </svg>
  );
};

const Confetti = ({ index }: { index: number }) => {
  const colors = ["#fbbf24", "#ec4899", "#60a5fa", "#a78bfa", "#34d399", "#f87171"];
  return (
    <div className="h-full w-full" style={{ backgroundColor: colors[index % colors.length] }} />
  );
};

const Firework = ({ index }: { index: number }) => {
  const palettes: Array<[string, string]> = [
    ["#fbbf24", "#fde68a"],
    ["#ec4899", "#fbcfe8"],
    ["#60a5fa", "#bae6fd"],
    ["#a78bfa", "#ddd6fe"],
    ["#34d399", "#a7f3d0"],
    ["#f87171", "#fecaca"],
  ];
  const [outer, inner] = (palettes[index % palettes.length] ?? palettes[0]) as [string, string];
  const outerCount = 24;
  const innerCount = 12;
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full overflow-visible">
      <title>Firework</title>
      <defs>
        <radialGradient id={`fw-glow-${index}`}>
          <stop offset="0%" stopColor="white" stopOpacity="0.95" />
          <stop offset="40%" stopColor={inner} stopOpacity="0.7" />
          <stop offset="100%" stopColor={inner} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="22" fill={`url(#fw-glow-${index})`} />
      {range(outerCount).map((spoke) => {
        const angle = (spoke * 2 * Math.PI) / outerCount;
        const reach = 38 + (spoke % 3) * 4;
        const startReach = 16;
        const x1 = 50 + Math.cos(angle) * startReach;
        const y1 = 50 + Math.sin(angle) * startReach;
        const x2 = 50 + Math.cos(angle) * reach;
        const y2 = 50 + Math.sin(angle) * reach;
        return (
          <g key={`outer-${spoke}`}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={outer}
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity="0.7"
            />
            <circle cx={x2} cy={y2} r="1.8" fill={outer} />
          </g>
        );
      })}
      {range(innerCount).map((spoke) => {
        const angle = (spoke * 2 * Math.PI) / innerCount + Math.PI / innerCount;
        const reach = 22;
        const x = 50 + Math.cos(angle) * reach;
        const y = 50 + Math.sin(angle) * reach;
        return <circle key={`inner-${spoke}`} cx={x} cy={y} r="1.4" fill={inner} opacity="0.9" />;
      })}
      <circle cx="50" cy="50" r="3" fill="white" />
    </svg>
  );
};

const ChristmasTheme = () => (
  <>
    <FloatingParticles
      count={60}
      baseClass="holiday-fall absolute -top-10"
      durationRange={[8, 14]}
      sizeRange={[5, 14]}
      drift
      renderItem={() => <Snowflake />}
    />
    <ChainedLights />
    <div className="holiday-sleigh-fly absolute h-20 w-44">
      <Sleigh />
    </div>
    <div className="holiday-tree-pop absolute -bottom-2 right-2 h-56 w-44 sm:h-72 sm:w-56">
      <ChristmasTree />
    </div>
    <div
      className="holiday-pop absolute bottom-2 right-2 flex h-20 w-72 items-end gap-2"
      style={{ animationDelay: "0.4s" }}
    >
      <div className="h-14 w-14">
        <GiftBox index={0} />
      </div>
      <div className="h-20 w-16">
        <GiftBox index={1} />
      </div>
      <div className="h-16 w-14">
        <GiftBox index={2} />
      </div>
      <div className="h-12 w-12">
        <GiftBox index={3} />
      </div>
    </div>
  </>
);

const NewYearTheme = () => (
  <>
    <FloatingParticles
      count={80}
      baseClass="holiday-fall absolute -top-10"
      durationRange={[5, 10]}
      sizeRange={[5, 11]}
      drift
      renderItem={(index) => <Confetti index={index} />}
    />
    <Bursts
      count={8}
      durationRange={[2.5, 4]}
      sizeRange={[120, 220]}
      className="holiday-firework"
      renderItem={(index) => <Firework index={index} />}
    />
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span
        className="holiday-flicker font-mono text-[16vw] font-extrabold leading-none text-amber-300"
        style={{ color: "#fbbf24" }}
      >
        {upcomingGregorianYear()}
      </span>
    </div>
  </>
);

const HalloweenTheme = () => (
  <>
    <div className="holiday-orange-tint absolute inset-0" />
    <div className="holiday-lightning absolute inset-0 bg-white" />
    <div className="absolute bottom-0 left-1/2 h-32 w-56 -translate-x-1/2 opacity-90 sm:h-40 sm:w-72">
      <SpookyHouse />
    </div>
    <HorizontalGliders
      count={6}
      reverse={false}
      durationRange={[10, 16]}
      sizeRange={[40, 64]}
      topRange={[8, 50]}
      renderItem={() => <Bat />}
    />
    <HorizontalGliders
      count={5}
      reverse
      durationRange={[12, 18]}
      sizeRange={[36, 60]}
      topRange={[15, 55]}
      renderItem={() => <Bat />}
    />
    <HorizontalGliders
      count={4}
      reverse={false}
      durationRange={[16, 22]}
      sizeRange={[36, 52]}
      topRange={[20, 60]}
      renderItem={() => <Ghost />}
    />
    <HorizontalGliders
      count={3}
      reverse
      durationRange={[18, 24]}
      sizeRange={[32, 48]}
      topRange={[25, 65]}
      renderItem={() => <Ghost />}
    />
    <div
      className="holiday-spider-descend absolute left-[20%] h-32 w-12"
      style={{ animationDelay: "1s" }}
    >
      <Spider />
    </div>
    <div
      className="holiday-spider-descend absolute left-[75%] h-32 w-12"
      style={{ animationDelay: "6s" }}
    >
      <Spider />
    </div>
    <div className="holiday-pumpkin-pop absolute -bottom-3 left-3 h-44 w-48 sm:h-56 sm:w-60">
      <Pumpkin />
    </div>
  </>
);

const ValentinesTheme = () => (
  <>
    <FloatingParticles
      count={48}
      baseClass="holiday-rise absolute"
      durationRange={[8, 14]}
      sizeRange={[14, 30]}
      startFromBottom
      drift
      renderItem={() => <Heart />}
    />
    <ProposalBanner text="Will you marry me, Inferize?" />
  </>
);

const StPatricksTheme = () => (
  <>
    <FloatingParticles
      count={50}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[7, 12]}
      sizeRange={[18, 32]}
      drift
      renderItem={() => <Shamrock />}
    />
    <div className="holiday-bloom absolute left-1/2 top-4 h-48 w-[80%] -translate-x-1/2 opacity-70">
      <Rainbow />
    </div>
    <div className="holiday-pop absolute -bottom-2 left-4 h-36 w-44">
      <PotOfGold />
    </div>
    <div
      className="holiday-pop absolute bottom-4 right-8 h-28 w-36"
      style={{ animationDelay: "0.3s" }}
    >
      <PotOfGold />
    </div>
    <div
      className="holiday-pop absolute bottom-12 left-1/3 h-24 w-32"
      style={{ animationDelay: "0.6s" }}
    >
      <PotOfGold />
    </div>
    <div
      className="holiday-pop absolute bottom-8 right-1/3 h-20 w-28"
      style={{ animationDelay: "0.9s" }}
    >
      <PotOfGold />
    </div>
  </>
);

const PiDayTheme = () => (
  <>
    {range(14).map((index) => {
      const left = (index / 14) * 100;
      const duration = 6 + sequencedRandom(index + 211) * 6;
      const delay = sequencedRandom(index + 223) * 6;
      return (
        <div
          key={index}
          className="holiday-matrix-fall absolute top-0 w-8"
          style={{
            left: `${left}%`,
            animationDuration: `${duration}s`,
            animationDelay: `${delay}s`,
          }}
        >
          <MatrixColumn index={index} />
        </div>
      );
    })}
    <PiDigitMarquee />
    <FloatingParticles
      count={20}
      baseClass="holiday-rise absolute"
      durationRange={[10, 16]}
      sizeRange={[30, 48]}
      startFromBottom
      drift
      renderItem={() => <RunningPi />}
    />
    <div
      className="holiday-hop-ltr absolute h-16 w-16"
      style={{ top: "62%", animationDuration: "11s" }}
    >
      <RunningPi />
    </div>
    <div
      className="holiday-hop-ltr absolute h-14 w-14"
      style={{ top: "78%", animationDuration: "9s", animationDelay: "4s" }}
    >
      <RunningPi />
    </div>
  </>
);

const HanukkahTheme = () => (
  <>
    <FloatingParticles
      count={18}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[8, 13]}
      sizeRange={[26, 44]}
      drift
      renderItem={() => <Sevivon />}
    />
    <FloatingParticles
      count={16}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[8, 13]}
      sizeRange={[28, 42]}
      drift
      renderItem={() => <Sufganiya />}
    />
    <FloatingParticles
      count={22}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[7, 12]}
      sizeRange={[18, 28]}
      drift
      renderItem={() => <Gelt />}
    />
    <div className="holiday-menorah-pop absolute -bottom-2 left-4 h-48 w-64 sm:h-56 sm:w-80">
      <Menorah />
    </div>
  </>
);

const PassoverTheme = () => (
  <>
    <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center opacity-60">
      <div className="h-24 w-40 sm:h-32 sm:w-56">
        <Pyramid />
      </div>
      <div className="h-32 w-52 sm:h-44 sm:w-72">
        <Pyramid />
      </div>
      <div className="h-20 w-36 sm:h-28 sm:w-48">
        <Pyramid />
      </div>
    </div>
    <FloatingParticles
      count={24}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[10, 16]}
      sizeRange={[26, 40]}
      drift
      renderItem={() => <Matzah />}
    />
    {range(5).map((index) => {
      const left = 10 + index * 18;
      const delay = sequencedRandom(index + 241) * 2;
      return (
        <div
          key={index}
          className="holiday-bob absolute bottom-12 h-20 w-14"
          style={{ left: `${left}%`, animationDelay: `${delay}s` }}
        >
          <WineCup />
        </div>
      );
    })}
    <div className="holiday-pop absolute bottom-4 right-6 h-32 w-24">
      <HaggadahScroll />
    </div>
    <div
      className="holiday-hop-ltr absolute h-12 w-16"
      style={{ top: "65%", animationDuration: "8s" }}
    >
      <Frog />
    </div>
    <div
      className="holiday-hop-ltr absolute h-10 w-14"
      style={{ top: "78%", animationDuration: "11s", animationDelay: "3s" }}
    >
      <Frog />
    </div>
  </>
);

const RoshHashanahTheme = () => (
  <>
    <FloatingParticles
      count={28}
      baseClass="holiday-fall absolute -top-10"
      durationRange={[8, 14]}
      sizeRange={[10, 18]}
      drift
      renderItem={() => <PomegranateSeed />}
    />
    <FloatingParticles
      count={14}
      baseClass="holiday-honey-drip absolute -top-10"
      durationRange={[5, 10]}
      sizeRange={[12, 22]}
      renderItem={() => <HoneyDrop />}
    />
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span
        className="holiday-flicker font-serif text-[18vw] font-extrabold leading-none"
        style={{ color: "#dc2626" }}
      >
        {upcomingHebrewYear()}
      </span>
    </div>
    <div className="holiday-pop absolute -bottom-2 right-4 h-48 w-48 sm:h-56 sm:w-56">
      <AppleHoney />
    </div>
    <div className="holiday-sway absolute top-6 left-4 h-20 w-44">
      <Shofar />
    </div>
  </>
);

const PurimTheme = () => (
  <>
    <FloatingParticles
      count={50}
      baseClass="holiday-fall absolute -top-10"
      durationRange={[5, 10]}
      sizeRange={[5, 11]}
      drift
      renderItem={(index) => <Confetti index={index} />}
    />
    <FloatingParticles
      count={10}
      baseClass="holiday-rise absolute"
      durationRange={[10, 16]}
      sizeRange={[44, 64]}
      startFromBottom
      drift
      renderItem={() => <VenetianMask />}
    />
    <FloatingParticles
      count={9}
      baseClass="holiday-rise absolute"
      durationRange={[11, 17]}
      sizeRange={[40, 58]}
      startFromBottom
      drift
      renderItem={() => <HarlequinMask />}
    />
    <FloatingParticles
      count={9}
      baseClass="holiday-rise absolute"
      durationRange={[12, 18]}
      sizeRange={[42, 60]}
      startFromBottom
      drift
      renderItem={() => <ComedyMask />}
    />
    <FloatingParticles
      count={12}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[9, 14]}
      sizeRange={[30, 44]}
      drift
      renderItem={() => <Hamantasch />}
    />
    <FloatingParticles
      count={10}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[8, 13]}
      sizeRange={[36, 52]}
      drift
      renderItem={() => <Gragger />}
    />
    <FloatingParticles
      count={10}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[10, 15]}
      sizeRange={[40, 58]}
      drift
      renderItem={() => <MishloachManot />}
    />
  </>
);

const YomHaatzmautTheme = () => (
  <>
    <FloatingParticles
      count={36}
      baseClass="holiday-fall holiday-spin absolute -top-10"
      durationRange={[7, 13]}
      sizeRange={[16, 28]}
      drift
      renderItem={() => <StarOfDavid />}
    />
    <div className="absolute left-4 top-4 flex items-start">
      <div className="h-72 w-3">
        <FlagPole />
      </div>
      <div className="holiday-flag-wave -ml-1 h-40 w-60 sm:h-48 sm:w-72">
        <IsraelFlagBig />
      </div>
    </div>
    <HorizontalGliders
      count={4}
      reverse={false}
      durationRange={[14, 20]}
      sizeRange={[50, 80]}
      topRange={[15, 50]}
      renderItem={() => <Dove />}
    />
    <HorizontalGliders
      count={2}
      reverse
      durationRange={[16, 22]}
      sizeRange={[44, 70]}
      topRange={[25, 55]}
      renderItem={() => <Dove />}
    />
  </>
);

const EasterTheme = () => (
  <>
    <FloatingParticles
      count={28}
      baseClass="holiday-rise absolute"
      durationRange={[10, 16]}
      sizeRange={[22, 38]}
      startFromBottom
      drift
      renderItem={(index) => <EasterEgg index={index} />}
    />
    <div
      className="holiday-hop-ltr absolute h-16 w-16"
      style={{ top: "70%", animationDuration: "12s" }}
    >
      <Bunny />
    </div>
    <div
      className="holiday-hop-ltr absolute h-14 w-14"
      style={{ top: "60%", animationDuration: "14s", animationDelay: "3s" }}
    >
      <Bunny />
    </div>
    <div
      className="holiday-hop-ltr absolute h-12 w-12"
      style={{ top: "80%", animationDuration: "10s", animationDelay: "6s" }}
    >
      <Bunny />
    </div>
    <div
      className="absolute bottom-0 left-0 right-0 flex items-end justify-around px-2"
      style={{ height: "60px" }}
    >
      {range(14).map((index) => (
        <div
          key={index}
          className="holiday-bloom h-12 w-10"
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          <Flower index={index} />
        </div>
      ))}
    </div>
  </>
);

export const OVERLAY_COMPONENTS: Record<OverlayId, () => ReactElement> = {
  christmas: ChristmasTheme,
  "new-year": NewYearTheme,
  halloween: HalloweenTheme,
  valentines: ValentinesTheme,
  "st-patricks": StPatricksTheme,
  "pi-day": PiDayTheme,
  hanukkah: HanukkahTheme,
  passover: PassoverTheme,
  "rosh-hashanah": RoshHashanahTheme,
  purim: PurimTheme,
  "yom-haatzmaut": YomHaatzmautTheme,
  easter: EasterTheme,
};
